import { useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { Alert, SocialPost } from '@/modules/market-signals/model/social-media';
import {
  formatTime,
  escapeRegex,
  getMatchedAlerts,
} from '@/modules/market-signals/util/social-media-news-utils';
import MediaRenderer from './media-renderer';

interface Props {
  post: SocialPost;
  alerts: Alert[];
  collapseMedia: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  smartMatching: boolean;
}

export default function SocialMediaPostItem({
  post,
  alerts,
  collapseMedia,
  expanded,
  onToggleExpand,
  smartMatching,
}: Props) {
  const highlightedContent = useMemo(() => {
    // highlight smaller alert words over larger ones
    if (alerts.length === 0) return <>{post.content}</>;

    const lowerContent = post.content.toLowerCase();
    const charAlerts: (Alert | null)[] = Array(post.content.length).fill(null);

    const sortedAlerts = [...alerts].sort((a, b) => a.word.length - b.word.length); // "p" before "post"

    for (const alert of sortedAlerts) {
      const word = alert.word.toLowerCase();
      const regex = new RegExp(escapeRegex(word), 'gi');
      let match: RegExpExecArray | null;

      while ((match = regex.exec(lowerContent))) {
        const start = match.index;
        const end = start + word.length;

        for (let i = start; i < end; i++) {
          if (charAlerts[i] === null) {
            charAlerts[i] = alert;
          }
        }
      }
    }

    const result: JSX.Element[] = [];
    let i = 0;
    while (i < post.content.length) {
      const currentAlert = charAlerts[i];
      let j = i + 1;
      while (j < post.content.length && charAlerts[j] === currentAlert) {
        j++;
      }

      const text = post.content.slice(i, j);
      if (currentAlert) {
        result.push(
          <span
            key={`highlight-${i}`}
            style={{ backgroundColor: currentAlert.color, color: '#fff' }}
          >
            {text}
          </span>,
        );
      } else {
        result.push(<span key={`text-${i}`}>{text}</span>);
      }

      i = j;
    }

    return <>{result}</>;
  }, [post.content, alerts]);

  const matched = useMemo(() => {
    return getMatchedAlerts(post.content, alerts, smartMatching).length > 0;
  }, [post.content, alerts, smartMatching]);

  const backgroundColor = matched ? 'rgba(255, 0, 0, 0.15)' : 'transparent';
  const isCollapsed = collapseMedia && post.mediaAttachments?.length && !expanded;

  return (
    <div className="border-bottom pt-1 pb-1" style={{ backgroundColor }}>
      <div className="mb-3 d-flex justify-content-between">
        <div className={'font-monospace text-muted small '}>{post.author.displayName}</div>
        <div className={'font-monospace text-muted small '}>
          {post.network} / @{post.author.username}
        </div>
      </div>

      <div className="mb-1 pe-1" style={{ userSelect: 'text', WebkitUserSelect: 'text' }}>
        <div style={{ whiteSpace: 'pre-wrap' }}>{highlightedContent}</div>
      </div>

      {post.mediaAttachments && post.mediaAttachments.length > 0 && (
        <div className="mb-1">
          {collapseMedia ? (
            isCollapsed ? (
              <Button size="sm" variant="outline-secondary" onClick={onToggleExpand}>
                Show Media
              </Button>
            ) : (
              <>
                <div>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="mb-1"
                    onClick={onToggleExpand}
                  >
                    Hide Media
                  </Button>
                </div>
                <MediaRenderer media={post.mediaAttachments} />
              </>
            )
          ) : (
            <MediaRenderer media={post.mediaAttachments} />
          )}
        </div>
      )}

      <div className="text-end text-muted small pe-2" style={{ userSelect: 'text' }}>
        {formatTime(post.createdAt)}
      </div>
    </div>
  );
}
